import { iconNames, uiNodeSchema } from "@sylviq/ui-core";
import type { ButtonVariant, Icon, UiNode } from "../../generated/react-web/types.js";
import { SuiRenderer, type CommandInvocation } from "../../generated/react-web/renderer.js";
import { cn } from "../../lib/cn.js";

export interface ExtensionViewAction {
  id: string;
  label: string;
  command: string;
  icon?: Icon;
  variant?: ButtonVariant;
}

export interface ExtensionViewSurface {
  viewId: string;
  body: UiNode;
  actions?: readonly ExtensionViewAction[];
  metadata?: Readonly<Record<string, string | number | boolean | null>>;
}

export interface ExtensionViewCommand extends CommandInvocation {}

const VIEW_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{1,79}$/;
const ACTION_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{1,79}$/;
const COMMAND = /^[A-Za-z0-9][A-Za-z0-9:._-]{1,119}$/;
const METADATA_KEY = /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/;
const BUTTON_VARIANTS: readonly ButtonVariant[] = ["primary", "secondary", "outline", "ghost"];
const MAX_ACTIONS = 12;
const MAX_METADATA_ENTRIES = 16;

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function action(value: unknown): ExtensionViewAction | null {
  const candidate = record(value);
  if (!candidate) return null;
  const { id, label, command, icon, variant } = candidate;
  if (
    typeof id !== "string" ||
    !ACTION_ID.test(id) ||
    typeof label !== "string" ||
    !label.trim() ||
    label.length > 100 ||
    typeof command !== "string" ||
    !COMMAND.test(command)
  ) {
    return null;
  }
  if (icon !== undefined && (typeof icon !== "string" || !(iconNames as readonly string[]).includes(icon))) {
    return null;
  }
  if (variant !== undefined && !BUTTON_VARIANTS.includes(variant as ButtonVariant)) {
    return null;
  }
  return {
    id,
    label,
    command,
    ...(icon === undefined ? {} : { icon: icon as Icon }),
    ...(variant === undefined ? {} : { variant: variant as ButtonVariant }),
  };
}

function metadata(value: unknown): ExtensionViewSurface["metadata"] | null {
  if (value === undefined) return undefined;
  const candidate = record(value);
  if (!candidate || Object.keys(candidate).length > MAX_METADATA_ENTRIES) return null;
  const result: Record<string, string | number | boolean | null> = {};
  for (const [key, item] of Object.entries(candidate)) {
    if (!METADATA_KEY.test(key)) return null;
    if (item === null || typeof item === "string" || typeof item === "boolean") {
      result[key] = item;
    } else if (typeof item === "number" && Number.isFinite(item)) {
      result[key] = item;
    } else {
      return null;
    }
  }
  return result;
}

/** Parse a runtime `result` from a successful v2 view response. */
export function parseExtensionViewSurface(value: unknown): ExtensionViewSurface | null {
  const candidate = record(value);
  if (!candidate) return null;
  const viewId = candidate.viewId;
  if (typeof viewId !== "string" || !VIEW_ID.test(viewId)) return null;
  const body = uiNodeSchema.safeParse(candidate.body);
  if (!body.success) return null;
  const actionsValue = candidate.actions;
  if (actionsValue !== undefined && !Array.isArray(actionsValue)) return null;
  if (Array.isArray(actionsValue) && actionsValue.length > MAX_ACTIONS) return null;
  const actions: ExtensionViewAction[] = [];
  for (const item of actionsValue ?? []) {
    const parsed = action(item);
    if (!parsed) return null;
    actions.push(parsed);
  }
  const parsedMetadata = metadata(candidate.metadata);
  if (parsedMetadata === null) return null;
  return {
    viewId,
    body: body.data,
    ...(actions.length === 0 ? {} : { actions }),
    ...(parsedMetadata === undefined ? {} : { metadata: parsedMetadata }),
  };
}

export function ExtensionViewBlock({
  surface,
  onCommand,
  className,
}: {
  surface: ExtensionViewSurface;
  onCommand: (invocation: ExtensionViewCommand) => void;
  className?: string;
}) {
  const actions = surface.actions ?? [];
  return (
    <div className={cn("grid gap-4", className)} data-extension-view={surface.viewId}>
      <SuiRenderer node={surface.body} onCommand={onCommand} />
      {actions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => (
            <SuiRenderer
              key={action.id}
              node={{
                type: "button",
                label: action.label,
                command: action.command,
                ...(action.icon === undefined ? {} : { icon: action.icon }),
                ...(action.variant === undefined ? {} : { variant: action.variant }),
              }}
              onCommand={onCommand}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
